// v0.6.10 — the statutory-period list that replaces "Jump to month".
//
// "Jump to month" never said what it jumped: clicking Mar could
// plausibly set the start, the end, or the whole month, and the only
// way to find out was to click and read the date fields. Worse, it
// framed the task as "construct a date range" when a VAT period is
// never a free choice — it is the statutory period of a return the
// client actually owes.
//
// This lists those returns instead, derived from the client's own
// registrations (see lib/filingPeriods.js). The two date inputs remain
// a first-class peer, not a buried fallback: amendments, enquiry
// re-runs and any client whose history the model can't express all
// need a free range.
import React, { useMemo, useState } from "react";
import { CaretDown, CaretRight, Database, Warning } from "@phosphor-icons/react";
import { buildPeriodOptions, cadencesFor, periodYear, MAX_PERIODS } from "../lib/filingPeriods";
import { presetRangeLabel } from "../workflow/periodPresets";
import { useTranslation } from "react-i18next";

const CADENCE_LABEL = {
  MONTHLY: "Monthly", QUARTERLY: "Quarterly",
  SEMI_ANNUALLY: "Half-yearly", ANNUALLY: "Annually",
};

const Badge = ({ tone, children, testid }) => {
  const tones = {
    ok: "bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 border-teal-300 dark:border-teal-700",
    partial: "bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700",
    none: "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600",
  };
  return (
    <span
      data-testid={testid}
      className={`text-[9px] px-1.5 py-0.5 rounded-full border shrink-0 ${tones[tone]}`}
    >{children}</span>
  );
};

export const FilingPeriodList = ({
  client, periodStart, periodEnd, dataRange, onSelect,
}) => {
  const { t } = useTranslation();
  const cadences = useMemo(() => cadencesFor(client), [client]);
  const [cadence, setCadence] = useState(null);
  // Default to the client's broadest cadence (quarterly over monthly)
  // — most filings are quarterly and it yields the shortest list.
  //
  // v0.6.10 sweep — the chosen cadence must be RE-VALIDATED against
  // the current client, not just remembered. This component is not
  // keyed by client, so switching entity (Change entity) keeps the
  // previous selection: picking Monthly on a client that files
  // monthly, then switching to a purely quarterly client, left
  // `cadence === "MONTHLY"` with no matching chip. The list then
  // rendered a full set of monthly periods for a client that files
  // none, with nothing highlighted to say where they came from.
  const available = cadences.map((c) => c.cadence);
  const active = (cadence && available.includes(cadence) ? cadence : null)
    || (available.includes("QUARTERLY") ? "QUARTERLY" : null)
    || available[0]
    || "QUARTERLY";

  const periods = useMemo(
    () => buildPeriodOptions({ client, cadence: active, dataRange }),
    [client, active, dataRange],
  );

  const years = useMemo(() => {
    const m = new Map();
    for (const p of periods) {
      const y = periodYear(p);
      if (!m.has(y)) m.set(y, []);
      m.get(y).push(p);
    }
    return [...m.entries()];
  }, [periods]);

  // Expand the newest year, plus whichever year holds the current
  // selection, so a backdated selection is never hidden behind a
  // collapsed header.
  const selectedYear = periods.find(
    (p) => p.start === periodStart && p.end === periodEnd)?.start.slice(0, 4);
  const [openYears, setOpenYears] = useState({});
  const isYearOpen = (y, i) => (openYears[y] === undefined
    ? (i === 0 || y === selectedYear)
    : openYears[y]);

  // v0.6.10 fix — this used to `return` an empty-state message INSTEAD
  // of the whole component, cadence chips included. Selecting a cadence
  // that yielded no periods therefore removed the only control that
  // could select a different one: a dead end with no way back except
  // reloading the page. The chips now always render, and the empty
  // message goes where the list would be.
  const activeInfo = cadences.find((c) => c.cadence === active);
  const unreadable = activeInfo?.unreadableDates || [];

  return (
    <div data-testid="filing-period-list">
      {cadences.length > 1 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {cadences.map((c) => (
            <button
              key={c.cadence}
              type="button"
              data-testid={`filing-period-cadence-${c.cadence}`}
              onClick={() => setCadence(c.cadence)}
              title={c.labels.join(", ")}
              className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                c.cadence === active
                  ? "bg-teal-600 text-white border-teal-500 font-bold"
                  : "bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-700 hover:border-teal-300"
              }`}
            >
              {/* Comma-separated, not space: these are distinct
                  registrations, and "DE IOSS" read as one. */}
              {t(`ui.calculate.period_list.cadence_${String(c.cadence).toLowerCase()}`, { defaultValue: CADENCE_LABEL[c.cadence] })} · {c.labels.join(", ")}
            </button>
          ))}
        </div>
      )}

      {!periods.length && (
        <div
          data-testid="filing-period-list-empty"
          className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed py-2"
        >
          {cadences.length === 0
            ? t("ui.calculate.period_list.empty_no_regs", { defaultValue: "No VAT registrations recorded for this client. Add them in Manage clients, or set the dates by hand on the right." })
            : t("ui.calculate.period_list.empty_no_periods", { defaultValue: "No periods could be derived for this cadence. Pick another cadence above, or set the dates by hand on the right." })}
        </div>
      )}

      <div className="max-h-[220px] overflow-y-auto pr-1 space-y-1">
        {years.map(([year, ps], i) => {
          const open = isYearOpen(year, i);
          return (
            <div key={year}>
              {years.length > 1 && (
                <button
                  type="button"
                  data-testid={`filing-period-year-${year}`}
                  onClick={() => setOpenYears((s) => ({ ...s, [year]: !open }))}
                  className="w-full flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 py-0.5"
                >
                  {open ? <CaretDown size={10} weight="bold" />
                        : <CaretRight size={10} weight="bold" />}
                  {year}
                  <span className="text-slate-400 dark:text-slate-600">
                    ({ps.length})
                  </span>
                </button>
              )}
              {open && ps.map((p) => {
                const selected = p.start === periodStart && p.end === periodEnd;
                return (
                  <button
                    key={p.key}
                    type="button"
                    data-testid={`filing-period-option-${p.key}`}
                    aria-pressed={selected}
                    onClick={() => onSelect({ start: p.start, end: p.end })}
                    className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-colors ${
                      selected
                        ? "bg-teal-600 text-white border-teal-500"
                        : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}
                  >
                    <span className={`tabular-nums text-[11px] font-bold w-16 shrink-0 ${
                      selected ? "text-white" : "text-slate-800 dark:text-slate-100"
                    }`}>{p.label}</span>
                    <span className={`tabular-nums text-[10px] flex-1 truncate ${
                      selected ? "text-teal-50" : "text-slate-500 dark:text-slate-400"
                    }`}>{presetRangeLabel(p.start, p.end)}</span>
                    {p.beforeRegistration && (
                      <Badge tone="partial" testid={`fp-pre-reg-${p.key}`}>
                        <Warning size={8} weight="bold" className="inline mr-0.5" />
                        {t("ui.calculate.period_list.badge_pre_reg", { defaultValue: "pre-reg" })}
                      </Badge>
                    )}
                    {dataRange && (
                      p.fullyCovered
                        ? <Badge tone="ok" testid={`fp-data-${p.key}`}>
                            <Database size={8} weight="bold" className="inline mr-0.5" />
                            {t("ui.calculate.period_list.badge_data", { defaultValue: "data" })}
                          </Badge>
                        : p.hasData
                          ? <Badge tone="partial" testid={`fp-partial-${p.key}`}>
                              {t("ui.calculate.period_list.badge_partial", { defaultValue: "partial" })}
                            </Badge>
                          : <Badge tone="none" testid={`fp-nodata-${p.key}`}>
                              {t("ui.calculate.period_list.badge_no_data", { defaultValue: "no data" })}
                            </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {periods.length >= MAX_PERIODS && (
        <div
          data-testid="filing-period-truncated"
          className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 leading-relaxed"
        >
          {t("ui.calculate.period_list.truncated", { max: MAX_PERIODS, defaultValue: `Showing the most recent ${MAX_PERIODS} periods. For anything older, set the dates by hand on the right.` })}
        </div>
      )}
      {unreadable.length > 0 && (
        <div
          data-testid="filing-period-unreadable-dates"
          className="text-[10px] text-amber-700 dark:text-amber-300 mt-2 leading-relaxed"
        >
          ⚠ {t("ui.calculate.period_list.unreadable_dates", { regs: unreadable.join(", "), defaultValue: `Effective-from date not understood for ${unreadable.join(", ")} — expected YYYY-MM-DD. These periods ignore it, so the list may start later than the real registration. Fix it in Manage clients.` })}
        </div>
      )}
      {!unreadable.length && periods.some((p) => p.assumedCadence) && (
        <div
          data-testid="filing-period-assumed-cadence"
          className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 leading-relaxed"
        >
          ⚠ {t("ui.calculate.period_list.assumed_cadence", { defaultValue: "No effective-from date on this client's registrations, so these periods assume today's declared frequency applied throughout. Verify before filing a backdated return." })}
        </div>
      )}
    </div>
  );
};
